/**
 * External dependencies
 */
import { useMemo } from 'react';
import { WOO_COLORS } from '../constants';
import { useColorPreference } from './use-color-preference';
import type { ChartTheme } from '@jetpack-premium-analytics/externals';

/**
 * The `@automattic/charts` theme plus the analytics-specific properties.
 */
export type WooChartTheme = ChartTheme & {
	leaderboardChart: ChartTheme[ 'leaderboardChart' ] & {
		barBorderRadius: string;
	};
};

export function useChartTheme(): WooChartTheme {
	const { preferences } = useColorPreference();

	return useMemo( () => {
		// If the user is using a custom color theme, use colors generated from the design system accent
		// color token, otherwise use the default analytics theme colors.
		const colors =
			preferences.interfaceTheme === 'custom'
				? [ '--wpds-color-foreground-interactive-brand' ]
				: WOO_COLORS;

		return {
			backgroundColor: 'var(--wpds-color-background-surface-neutral-strong, #fff)',
			labelBackgroundColor: 'var(--wpds-color-background-interactive-neutral-weak, #0000)',
			labelTextColor: 'var(--wpds-color-foreground-interactive-neutral-strong, #f0f0f0)',
			colors,
			gridStyles: {
				stroke: 'var(--wpds-color-stroke-surface-neutral, #dbdbdb)',
				strokeWidth: 1,
			},
			tickLength: 4,
			gridColor: '',
			gridColorDark: '',
			svgLabelSmall: {
				fill: 'var(--wpds-color-foreground-content-neutral-weak, #707070)',
			},
			xTickLineStyles: { stroke: '' },
			xAxisLineStyles: {
				stroke: 'var(--wpds-color-stroke-surface-neutral, #dbdbdb)',
				strokeWidth: 1,
			},
			legend: {
				labelStyles: {
					fontSize: 'var(--wpds-typography-font-size-sm, 12px)',
					fontWeight: 400,
					color: 'var(--wpds-color-foreground-content-neutral, #1e1e1e)',
				},
				containerStyles: {
					rowGap: 'var(--wpds-dimension-padding-sm, 8px)',
					columnGap: 'var(--wpds-dimension-padding-sm, 8px)',
				},
				shapeStyles: [
					{
						transform: 'translate(0, 1px)',
					},
					{
						transform: 'translate(0, 1px)',
						strokeDasharray: '2, 2, 3, 2, 3, 2, 2',
					},
				],
			},
			leaderboardChart: {
				rowGap: 12,
				columnGap: 4,
				labelSpacing: 'xs',
				barBorderRadius: 'var(--wpds-border-radius-md, 4px)',
				deltaColors: [
					'var(--wpds-color-stroke-surface-error-strong, #cc1818)',
					'var(--wpds-color-foreground-content-neutral-weak, #707070)',
					'var(--wpds-color-stroke-surface-success-strong, #008030)',
				] as [ string, string, string ], // [ negative, neutral, positive ]
			},
			conversionFunnelChart: {
				backgroundColor: 'var(--wpds-color-background-surface-brand, color-mix(in oklch, var(--wp-admin-theme-color, #3858e9) 9%, white))',
				positiveChangeColor: 'var(--wpds-color-foreground-content-success-weak, #008030)',
				negativeChangeColor: 'var(--wpds-color-foreground-content-error-weak, #cc1818)',
			},
			lineChart: {
				lineStyles: {
					comparison: {
						strokeDasharray: '4 4',
						strokeWidth: 1.5,
						strokeLinecap: 'square' as const,
						strokeOpacity: 0.8,
						strokeDashoffset: 2,
					},
				},
			},
			seriesLineStyles: [
				{
					strokeWidth: 2,
				},
				{
					strokeDasharray: '4 4',
					strokeWidth: 1.5,
					strokeLinecap: 'square' as const,
					strokeDashoffset: 2,
				},
			],
		};
	}, [ preferences.interfaceTheme ] );
}
